import { inject, Injectable, PLATFORM_ID, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Observable, tap, map } from 'rxjs';
import { ConfigService } from '../config/config.service';

export interface Company {
  id: string;
  name: string;
  nip?: string;
  pictureUrl?: string;
}

export interface CompanyAddress {
  street?: string;
  building?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  county?: string;
  municipality?: string;
  voivodeship?: string;
  country_code?: string;
}

export interface CompanyBasicDataFull {
  name?: string;
  nip?: string;
  vatin_country_code?: string;
  address?: CompanyAddress;
  phone?: string;
  email?: string;
  natural_person?: boolean;
}

export type CompanyPaymentMethodType = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type OwnBankAccountType = 1 | 2 | 3;

export interface CompanyBankAccount {
  accountNumber: string;
  swift?: string;
  bankName?: string;
  description?: string;
  ownAccountType?: OwnBankAccountType;
}

export interface CompanyPaymentMethod {
  id: string;
  type: CompanyPaymentMethodType;
  label: string;
  currency: string;
  isDefault: boolean;
  bankAccount?: CompanyBankAccount;
  otherDescription?: string;
}

export interface CompanySettings {
  id: string;
  picture_url?: string;
  basic_data: CompanyBasicDataFull;
  eu_prefix?: string;
  eori?: string;
  seller_status?: number;
  correspondence_address?: CompanyAddress;
  invoice_number_pattern?: InvoiceNumberPattern;
  tax_office_code?: string;
  taxation_form?: string;
  payment_data?: CompanyPaymentMethod[];
  contact_persons?: unknown[];
  document_defaults?: DocumentDefaults;
}

export interface InvoiceNumberPattern {
  prefix?: string;
  type: string;
  pad_number?: boolean;
  min_number_length?: number;
  document_type_prefixes?: Record<string, string>;
}

export type PaymentTermPreset = '1d' | '2d' | '3d' | '7d' | '14d' | '30d' | 'custom';
export type DateFormatPreset = 'DD.MM.YYYY' | 'YYYY-MM-DD' | 'DD/MM/YYYY' | 'MM/DD/YYYY';

export interface DocumentDefaults {
  defaultVatRate?: string;
  defaultAnnotation?: string;
  defaultPaymentTerm?: PaymentTermPreset;
  defaultPaymentTermDays?: number;
  defaultDateFormat?: DateFormatPreset;
}

export interface UpdateCompanyRequest {
  picture_url?: string;
  basic_data?: CompanyBasicDataFull;
  eu_prefix?: string;
  eori?: string;
  seller_status?: number;
  correspondence_address?: CompanyAddress;
  invoice_number_pattern?: InvoiceNumberPattern;
  tax_office_code?: string;
  taxation_form?: string;
  payment_data?: CompanyPaymentMethod[];
  document_defaults?: DocumentDefaults;
}

const COMPANY_STORAGE_KEY = 'selected_company_id';

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly config = inject(ConfigService);

  private readonly _companies = signal<Company[]>([]);
  private readonly _selectedCompanyId = signal<string | null>(this.loadSelectedCompanyId());

  readonly companies = this._companies.asReadonly();
  readonly selectedCompanyId = this._selectedCompanyId.asReadonly();

  readonly selectedCompany = computed(() => {
    const id = this._selectedCompanyId();
    return this._companies().find((c) => c.id === id) ?? null;
  });

  readonly hasMultipleCompanies = computed(() => this._companies().length > 1);

  loadUserCompanies(): Observable<Company[]> {
    return this.http
      .post<{ data: CompanyApiItem[] }>(`${this.config.apiUrl()}/user-companies/list`, {
        limit: 100,
      })
      .pipe(
        map((res) => res.data.map(mapCompany)),
        tap((companies) => {
          this._companies.set(companies);
          if (companies.length > 0 && !this.selectedCompany()) {
            this.selectCompany(companies[0].id);
          }
        }),
      );
  }

  getSettings(): Observable<CompanySettings> {
    const id = this._selectedCompanyId();
    return this.http.get<CompanySettings>(`${this.config.apiUrl()}/companies/${id}`);
  }

  updateSettings(data: UpdateCompanyRequest): Observable<void> {
    const id = this._selectedCompanyId();
    return this.http.patch<void>(`${this.config.apiUrl()}/companies/${id}`, data).pipe(
      tap(() => {
        if (data.basic_data?.name) {
          this._companies.update((list) =>
            list.map((c) => (c.id === id ? { ...c, name: data.basic_data!.name! } : c)),
          );
        }
      }),
    );
  }

  selectCompany(id: string): void {
    this._selectedCompanyId.set(id);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(COMPANY_STORAGE_KEY, id);
    }
  }

  clear(): void {
    this._companies.set([]);
    this._selectedCompanyId.set(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(COMPANY_STORAGE_KEY);
    }
  }

  private loadSelectedCompanyId(): string | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(COMPANY_STORAGE_KEY);
    } catch {
      return null;
    }
  }
}

interface CompanyApiItem {
  id: string;
  basic_data?: { name?: string; nip?: string };
  picture_url?: string;
}

function mapCompany(item: CompanyApiItem): Company {
  return {
    id: item.id,
    name: item.basic_data?.name ?? item.id,
    nip: item.basic_data?.nip,
    pictureUrl: item.picture_url,
  };
}
